import {areSetsEqual} from '@benchling/js-utils';

import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CreateStepParams} from 'pipelines/network/PipelineDAO';
import APIUtil from 'util/APIUtil';

import {
  COPY_STEPS_CLIPBOARD_CONTENT_TYPE,
  CopyStepsClipboardContent,
  ParseClipboardContentFn,
} from './CopyPasteTypes';

function isCopyStepsClipboardContent(value: unknown): value is CopyStepsClipboardContent {
  if (
    value == null ||
    typeof value !== 'object' ||
    !areSetsEqual(new Set(Object.keys(value)), new Set(['type', 'steps']))
  ) {
    return false;
  }
  const {type, steps} = value as CopyStepsClipboardContent;
  return (
    type === COPY_STEPS_CLIPBOARD_CONTENT_TYPE &&
    steps.every(
      (step: unknown) => typeof step === 'object' && step != null && step.hasOwnProperty('stepType')
    )
  );
}

function translateDataCatalogDatasetStepsForTemplate(
  steps: Array<CreateStepParams>
): Array<CreateStepParams> {
  const replacedStepIndexes = new Set<number>();

  const translatedSteps = steps.map((step, index) => {
    if (step.stepType !== PipelineStepType.DATA_CATALOG_DATASET) {
      return step;
    }
    const {datasetId, dataFrameName} = step.config ?? {};
    replacedStepIndexes.add(index);
    return {
      ...step,
      stepType: PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
      config: {datasetId, name: dataFrameName, description: '', requireColumnMapping: true},
    };
  });

  return translatedSteps.map((step) => {
    if (step.incomingEdges == null || step.incomingEdges.length === 0) {
      return step;
    }
    return {
      ...step,
      incomingEdges: step.incomingEdges.map((edge) =>
        'fromStepIndex' in edge && replacedStepIndexes.has(edge.fromStepIndex)
          ? {...edge, fromStepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}
          : edge
      ),
    };
  });
}

/** @package */
export const parseStepUpsertsClipboardContent: ParseClipboardContentFn = (pipelineId, text) => {
  try {
    const json = JSON.parse(text);
    if (isCopyStepsClipboardContent(json) && json.steps.length > 0) {
      const steps = APIUtil.isAnalysisTemplate(pipelineId)
        ? translateDataCatalogDatasetStepsForTemplate(json.steps)
        : json.steps;
      return Promise.resolve(steps);
    }
  } catch (e) {
    // `SyntaxError` is raised if the clipboard content is not JSON; all other exceptions are unexpected.
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }
  return Promise.resolve(null);
};
